import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LookupModalService } from './services/lookup-modal.service';
import { OrderByPipe } from 'src/app/shared/pipe/order-by.pipe';
import { OfficeFieldWebInputType } from '@pages/office/models/office-data.model';
import { PAGINATION_DEFAULTS } from 'src/app/shared/constants/pagination.constants';

@Component({
    selector: 'app-lookup-modal',
    templateUrl: './lookup-modal.component.html',
    styleUrls: ['./lookup-modal.component.scss'],
    standalone: false
})
export class LookupModalComponent implements OnInit {
  orderByPipe: OrderByPipe;
  rowOrderValue: string;
  filterValue: string | null;
  selectedLookupCategory: string;
  reverseSortOrder: boolean | null;
  retrievingLookups: boolean;
  columnHeaders: string[];
  lookupRows: object[];
  filteredRows: object[];
  pageRows: object[];
  selectedRow: object | null;
  paginationData: {
    maxItemsPerPage: number;
    maxPageCountDisplay: number;
    totalItemCount: number;
    currentPage: number;
  };
  @Input() modalTitle: string;
  @Input() lookupParams: {
    co: number;
    form: string;
    seq: number;
    lookup: string;
    formData: string;
    showAllColumns: boolean;
    searchString: string;
  };
  @Input() availableLookups: {
    LoadSeq: number;
    Lookup: string;
    Title: string;
  }[];
  @Input() webInputType: OfficeFieldWebInputType;
  @Input() mask: string;

  constructor(
    private lookupService: LookupModalService,
    private ngbActiveModal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.orderByPipe = new OrderByPipe();
    this.rowOrderValue = '';
    this.filterValue = null;
    this.reverseSortOrder = null;
    this.retrievingLookups = false;
    this.columnHeaders = [];
    this.lookupRows = [];
    this.filteredRows = [];
    this.pageRows = [];
    this.selectedRow = null;
    this.paginationData = { ...PAGINATION_DEFAULTS };
    this.setLookupCategory();
    this.getLookupList();
  }

  setLookupCategory(): void {
    if (this.availableLookups.length > 1) {
      this.availableLookups.sort((a, b) => a.LoadSeq - b.LoadSeq);
      this.selectedLookupCategory = this.availableLookups[0].Lookup;
    } else {
      this.selectedLookupCategory = this.lookupParams.lookup;
    }
  }

  async getLookupList(): Promise<void> {
    this.retrievingLookups = true;
    try {
      const response: any = await this.lookupService.getLookup(this.lookupParams); // TODO: switch to observable to cancel request and processing when modal is closed (E.g.:to not show HTTP error).
      this.columnHeaders = Object.keys(response[0] || {});
      this.lookupRows = response;
      this.filteredRows = this.lookupRows;
      this.paginationData.totalItemCount = this.filteredRows.length;
      this.updatePageRows(this.paginationData.currentPage);
    } finally {
      this.retrievingLookups = false;
    }
  }

  closeLookupModal(): void {
    this.ngbActiveModal.close(this.selectedRow);
  }

  orderTableByValue(orderValue: string): void {
    if (this.rowOrderValue === orderValue) {
      this.reverseSortOrder = !this.reverseSortOrder;
    } else {
      this.rowOrderValue = orderValue;
    }
    this.filteredRows = this.orderByPipe.transform(this.filteredRows, [orderValue], this.reverseSortOrder ? 'desc' : 'asc');
    this.updatePageRows(this.paginationData.currentPage);
  }

  filterLookupTable(): void {
    this.filteredRows = this.lookupRows.filter(row => this.rowContainsSubstring(row));
    this.paginationData.totalItemCount = this.filteredRows.length;
    this.paginationData.currentPage = PAGINATION_DEFAULTS.currentPage;
    this.updatePageRows(this.paginationData.currentPage);
  }

  rowContainsSubstring(row: object): boolean {
    let rowContainsSubstring = false;
    this.columnHeaders.forEach(key => {
      if (row[key] != null && row[key].toString().toLowerCase().includes(this.filterValue.toLowerCase())) {
        rowContainsSubstring = true;
        return;
      }
    });
    return rowContainsSubstring;
  }

  selectRow(row: object): void {
    this.selectedRow = row;
    this.closeLookupModal();
  }

  async triggerLookup(): Promise<void> {
    if (this.selectedLookupCategory != null) {
      this.lookupParams.lookup = this.selectedLookupCategory;
      this.modalTitle = this.availableLookups?.find(l => l.Lookup === this.selectedLookupCategory)?.Title;
      this.filteredRows = [];
      this.pageRows = [];
      await this.getLookupList();
    }
  }

  updatePageRows(selectedPage: number): void {
    const begin = (selectedPage - 1) * this.paginationData.maxItemsPerPage;
    const end = begin + this.paginationData.maxItemsPerPage;
    this.pageRows = this.filteredRows.slice(begin, end);
  }
}
